import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { BuilderState, BuilderAction, UnifiedChatbotData, UnifiedChatbotSettings, DEFAULT_UNIFIED_SETTINGS } from '@/types/builder';
import { ChatTheme, Question, TriggerRule } from '@/types/theme';
import { useToast } from '@/components/ui/use-toast';
import { useChatbotBuilder } from '@/hooks/useChatbotBuilder';

// Initial state
const initialState: BuilderState = {
  chatbot: null,
  isLoading: false,
  isSaving: false,
  error: null,
  isDirty: false,
  dirtyFields: new Set(),
  activeTab: 'dashboard',
  unsavedChanges: false
};

// Reducer function
const builderReducer = (state: BuilderState, action: BuilderAction): BuilderState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_SAVING':
      return { ...state, isSaving: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_CHATBOT':
      return { 
        ...state, 
        chatbot: action.payload,
        isLoading: false,
        error: null
      };
    
    case 'UPDATE_SETTINGS':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          settings: { ...state.chatbot.settings, ...action.payload }
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_THEME':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          theme: { ...state.chatbot.theme, ...action.payload }
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_QUESTIONS':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          questions: action.payload
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_TRIGGER_RULES':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          triggerRules: action.payload
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'UPDATE_BASIC_INFO':
      if (!state.chatbot) return state;
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          ...action.payload
        },
        isDirty: true,
        unsavedChanges: true
      };
    
    case 'SET_DIRTY':
      const newDirtyFields = new Set(state.dirtyFields);
      if (action.payload.isDirty) {
        newDirtyFields.add(action.payload.field);
      } else {
        newDirtyFields.delete(action.payload.field);
      }
      return {
        ...state,
        dirtyFields: newDirtyFields,
        isDirty: newDirtyFields.size > 0,
        unsavedChanges: newDirtyFields.size > 0
      };
    
    case 'RESET_DIRTY':
      return {
        ...state,
        isDirty: false,
        dirtyFields: new Set(),
        unsavedChanges: false
      };
    
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    
    default:
      return state;
  }
};

// Context interface
interface BuilderContextType {
  state: BuilderState;
  dispatch: React.Dispatch<BuilderAction>;
  
  // Convenience methods
  updateSettings: (updates: Partial<UnifiedChatbotSettings>) => void;
  updateTheme: (updates: Partial<ChatTheme>) => void;
  updateQuestions: (questions: Question[]) => void;
  updateTriggerRules: (rules: TriggerRule[]) => void;
  updateBasicInfo: (info: { name?: string; description?: string; title?: string; welcome_message?: string; completion_message?: string }) => void;
  setDirty: (field: string, isDirty: boolean) => void;
  resetDirty: () => void;
  setActiveTab: (tab: string) => void;
  
  // Data access helpers
  getChatbot: () => UnifiedChatbotData | null;
  getSettings: () => UnifiedChatbotSettings;
  getTheme: () => ChatTheme | null;
  getQuestions: () => Question[];
  getTriggerRules: () => TriggerRule[];

  // Save methods
  debouncedSave: (updates: Partial<UnifiedChatbotData>) => void;
  saveTheme: (theme: ChatTheme) => void;
  saveTriggerRules: (triggerRules: TriggerRule[]) => void;
  refetch: () => void;
}

// Create context
const BuilderContext = createContext<BuilderContextType | undefined>(undefined);

// Provider component
interface BuilderProviderProps {
  children: React.ReactNode;
  chatbotId: string;
}

export const BuilderProvider: React.FC<BuilderProviderProps> = ({ children, chatbotId }) => {
  const [state, dispatch] = useReducer(builderReducer, initialState);
  const { toast } = useToast();



  // Use the unified chatbot builder hook as the single source of truth
  const {
    data: unifiedData,
    isLoading,
    error,
    isSaving,
    debouncedSave,
    saveTheme,
    saveTriggerRules,
    refetch
  } = useChatbotBuilder(chatbotId);

  // Update context state when unified data changes
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, [error]);

  useEffect(() => {
    dispatch({ type: 'SET_SAVING', payload: isSaving });
  }, [isSaving]);

  useEffect(() => {
    if (unifiedData) {
      dispatch({ type: 'SET_CHATBOT', payload: unifiedData });
      // Reset dirty state when fresh data is loaded (after save)
      dispatch({ type: 'RESET_DIRTY' });
    }
  }, [unifiedData]);

  // Convenience methods
  const updateSettings = useCallback((updates: Partial<UnifiedChatbotSettings>) => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: updates });
  }, []);

  const updateTheme = useCallback((updates: Partial<ChatTheme>) => {
    dispatch({ type: 'UPDATE_THEME', payload: updates });
  }, []);

  const updateQuestions = useCallback((questions: Question[]) => {
    dispatch({ type: 'UPDATE_QUESTIONS', payload: questions });
  }, []);

  const updateTriggerRules = useCallback((rules: TriggerRule[]) => {
    dispatch({ type: 'UPDATE_TRIGGER_RULES', payload: rules });

    // Auto-save trigger rules when they change
    if (rules && rules.length > 0) {
      console.log('🎯 Auto-saving trigger rules after update...');
      saveTriggerRules(rules);
    }
  }, [saveTriggerRules]);

  const updateBasicInfo = useCallback((info: { name?: string; description?: string; title?: string; welcome_message?: string; completion_message?: string }) => {
    dispatch({ type: 'UPDATE_BASIC_INFO', payload: info });
  }, []);

  const setDirty = useCallback((field: string, isDirty: boolean) => {
    dispatch({ type: 'SET_DIRTY', payload: { field, isDirty } });
  }, []);

  const resetDirty = useCallback(() => {
    dispatch({ type: 'RESET_DIRTY' });
  }, []);

  const setActiveTab = useCallback((tab: string) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  }, []);

  // Data access helpers
  const getChatbot = useCallback(() => state.chatbot, [state.chatbot]);
  
  const getSettings = useCallback((): UnifiedChatbotSettings => {
    return state.chatbot?.settings || DEFAULT_UNIFIED_SETTINGS;
  }, [state.chatbot]);

  const getTheme = useCallback(() => state.chatbot?.theme || null, [state.chatbot]);
  
  const getQuestions = useCallback(() => state.chatbot?.questions || [], [state.chatbot]);
  
  const getTriggerRules = useCallback(() => state.chatbot?.triggerRules || [], [state.chatbot]);

  // Context value
  const contextValue: BuilderContextType = {
    state,
    dispatch,
    updateSettings,
    updateTheme,
    updateQuestions,
    updateTriggerRules,
    updateBasicInfo,
    setDirty,
    resetDirty,
    setActiveTab,
    getChatbot,
    getSettings,
    getTheme,
    getQuestions,
    getTriggerRules,
    debouncedSave,
    saveTheme,
    saveTriggerRules,
    refetch
  };

  return (
    <BuilderContext.Provider value={contextValue}>
      {children}
    </BuilderContext.Provider>
  );
};

// Hook to use the context
export const useBuilder = () => {
  const context = useContext(BuilderContext);
  if (context === undefined) {
    throw new Error('useBuilder must be used within a BuilderProvider');
  }
  return context;
};

// Hook to use builder with automatic save
export const useBuilderWithSave = (debounceMs: number = 1000) => {
  const builder = useBuilder();
  const { toast } = useToast();

  // TODO: Implement debounced save logic
  // This will be implemented in the next step with the unified data fetching hook

  return {
    ...builder,
    saveChanges: () => {
      // Placeholder for save functionality
      console.log('Save changes called');
    }
  };
};
